export type SceneNodeType = 'Character' | 'Object' | 'Environment' | 'LightingSource' | 'Camera' | 'Effect';

export type SceneNode = {
  id: string;
  label: string;
  type: SceneNodeType;
  depth: 'foreground' | 'midground' | 'background';
};

export type SceneRelationType =
  | 'in front of'
  | 'behind'
  | 'above'
  | 'below'
  | 'holding'
  | 'illuminated by'
  | 'looking at'
  | 'moving toward'
  | 'focusing on'
  | 'standing in';

export type SceneEdge = {
  source: string;
  relation: SceneRelationType;
  target: string;
};

export type SceneGraphConflict = {
  message: string;
  edgeIndexes: number[];
};

export type SceneComplexityMetrics = {
  nodeCount: number;
  edgeCount: number;
  density: number;
  characterCount: number;
  objectCount: number;
  environmentCount: number;
  effectCount: number;
  complexityLevel: 'simple' | 'moderate' | 'complex' | 'dense';
};

export type SceneOptimizationHint = {
  hint: string;
  reason: string;
};

export type VisualSceneGraph = {
  nodes: SceneNode[];
  edges: SceneEdge[];
  conflicts: SceneGraphConflict[];
  metrics: SceneComplexityMetrics;
  optimization: SceneOptimizationHint[];
};

const mkNodeId = (label: string, i: number): string => `${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${i}`;

const addUniqueNode = (nodes: SceneNode[], label: string, type: SceneNodeType, depth: SceneNode['depth']): SceneNode => {
  const found = nodes.find((n) => n.label.toLowerCase() === label.toLowerCase());
  if (found) return found;
  const node: SceneNode = { id: mkNodeId(label, nodes.length + 1), label, type, depth };
  nodes.push(node);
  return node;
};

const detectEntities = (prompt: string): SceneNode[] => {
  const lower = prompt.toLowerCase();
  const nodes: SceneNode[] = [];

  const chars = ['knight', 'warrior', 'character', 'person', 'man', 'woman', 'hero', 'cat', 'dog', 'robot'];
  const objects = ['sword', 'shield', 'car', 'tank', 'book', 'gun', 'lantern', 'crown'];
  const envs = ['castle', 'desert', 'forest', 'city', 'street', 'temple', 'ocean', 'mountain'];
  const lights = ['sunset', 'sunlight', 'moonlight', 'torch', 'neon light', 'key light'];
  const effects = ['fog', 'rain', 'smoke', 'dust', 'fire', 'sparks', 'volumetric light'];

  chars.forEach((w) => lower.includes(w) && addUniqueNode(nodes, w, 'Character', 'foreground'));
  objects.forEach((w) => lower.includes(w) && addUniqueNode(nodes, w, 'Object', 'midground'));
  envs.forEach((w) => lower.includes(w) && addUniqueNode(nodes, w, 'Environment', 'background'));
  lights.forEach((w) => lower.includes(w) && addUniqueNode(nodes, w, 'LightingSource', 'background'));
  effects.forEach((w) => lower.includes(w) && addUniqueNode(nodes, w, 'Effect', 'midground'));

  addUniqueNode(nodes, 'camera', 'Camera', 'foreground');

  if (!nodes.some((n) => n.type === 'Character')) {
    addUniqueNode(nodes, 'main subject', 'Character', 'foreground');
  }

  return nodes;
};

const byLabel = (nodes: SceneNode[], label: string): SceneNode | null =>
  nodes.find((n) => n.label.toLowerCase() === label.toLowerCase()) || null;

const firstByType = (nodes: SceneNode[], type: SceneNodeType): SceneNode | null => nodes.find((n) => n.type === type) || null;

const buildRelationships = (prompt: string, nodes: SceneNode[]): SceneEdge[] => {
  const lower = prompt.toLowerCase();
  const edges: SceneEdge[] = [];

  const character = firstByType(nodes, 'Character');
  const environment = firstByType(nodes, 'Environment');
  const lighting = firstByType(nodes, 'LightingSource');
  const camera = byLabel(nodes, 'camera');
  const object = firstByType(nodes, 'Object');

  if (character && object && /holding|holds|carry|carrying/.test(lower)) {
    edges.push({ source: character.id, relation: 'holding', target: object.id });
  }

  if (character && environment) {
    if (/in front of/.test(lower)) edges.push({ source: character.id, relation: 'in front of', target: environment.id });
    else if (/behind/.test(lower)) edges.push({ source: character.id, relation: 'behind', target: environment.id });
    else edges.push({ source: character.id, relation: 'standing in', target: environment.id });
  }

  if (lighting && character) edges.push({ source: lighting.id, relation: 'illuminated by', target: character.id });
  if (camera && character) edges.push({ source: camera.id, relation: 'focusing on', target: character.id });

  if (character && /looking at/.test(lower)) {
    const targetObject = nodes.find((n) => n.id !== character.id && (n.type === 'Object' || n.type === 'Character' || n.type === 'Environment'));
    if (targetObject) edges.push({ source: character.id, relation: 'looking at', target: targetObject.id });
  }

  if (character && /moving toward|towards|approaching/.test(lower)) {
    const targetObject = nodes.find((n) => n.id !== character.id && (n.type === 'Object' || n.type === 'Environment' || n.type === 'Character'));
    if (targetObject) edges.push({ source: character.id, relation: 'moving toward', target: targetObject.id });
  }

  return edges;
};

const detectSpatialConflicts = (edges: SceneEdge[]): SceneGraphConflict[] => {
  const conflicts: SceneGraphConflict[] = [];

  edges.forEach((edge, i) => {
    edges.forEach((other, j) => {
      if (i >= j) return;
      if (edge.source !== other.source || edge.target !== other.target) return;

      const opposite =
        (edge.relation === 'in front of' && other.relation === 'behind') ||
        (edge.relation === 'behind' && other.relation === 'in front of') ||
        (edge.relation === 'above' && other.relation === 'below') ||
        (edge.relation === 'below' && other.relation === 'above');

      if (opposite) {
        conflicts.push({
          message: 'Spatial contradiction: element cannot be in opposing positions simultaneously.',
          edgeIndexes: [i, j],
        });
      }
    });
  });

  return conflicts;
};

const buildMetrics = (nodes: SceneNode[], edges: SceneEdge[]): SceneComplexityMetrics => {
  const nodeCount = nodes.length;
  const edgeCount = edges.length;
  const density = nodeCount <= 1 ? 0 : edgeCount / (nodeCount * (nodeCount - 1));

  const characterCount = nodes.filter((n) => n.type === 'Character').length;
  const objectCount = nodes.filter((n) => n.type === 'Object').length;
  const environmentCount = nodes.filter((n) => n.type === 'Environment').length;
  const effectCount = nodes.filter((n) => n.type === 'Effect').length;

  const complexityLevel = nodeCount < 4 ? 'simple' : nodeCount < 7 ? 'moderate' : nodeCount < 11 ? 'complex' : 'dense';

  return { nodeCount, edgeCount, density, characterCount, objectCount, environmentCount, effectCount, complexityLevel };
};

const optimizeSceneGraph = (graph: VisualSceneGraph): SceneOptimizationHint[] => {
  const hints: SceneOptimizationHint[] = [];

  if (!graph.nodes.some((n) => n.type === 'LightingSource')) {
    hints.push({ hint: 'Add a motivated lighting source node.', reason: 'Lighting source improves scene readability and realism.' });
  }

  if (!graph.nodes.some((n) => n.type === 'Environment')) {
    hints.push({ hint: 'Add an environment anchor.', reason: 'Environment grounding improves composition and scale coherence.' });
  }

  if (graph.metrics.complexityLevel === 'dense' && graph.edges.length < graph.nodes.length / 2) {
    hints.push({ hint: 'Increase explicit relationships between key nodes.', reason: 'Dense scenes need clearer relational grammar for image models.' });
  }

  if (graph.conflicts.length) {
    hints.push({ hint: 'Resolve spatial contradictions before rendering.', reason: 'Conflicting spatial relations can destabilize composition.' });
  }

  return hints;
};

export const buildVisualSceneGraph = (prompt: string): VisualSceneGraph => {
  const nodes = detectEntities(prompt);
  const edges = buildRelationships(prompt, nodes);
  const conflicts = detectSpatialConflicts(edges);
  const metrics = buildMetrics(nodes, edges);

  const graph: VisualSceneGraph = { nodes, edges, conflicts, metrics, optimization: [] };
  graph.optimization = optimizeSceneGraph(graph);
  return graph;
};
